import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const {
  FiUsers, FiUserPlus, FiMail, FiX, FiCheck, FiShield, FiEdit3,
  FiMessageSquare, FiImage, FiTrash2, FiRefreshCw, FiClock,
  FiAlertCircle, FiInfo, FiChevronDown, FiChevronUp, FiSend,
} = FiIcons;

// ── Role definitions ──────────────────────────────────────────────────────────
const CO_ADMIN_ROLES = {
  editor: {
    id: 'editor',
    label: 'Editor',
    icon: FiEdit3,
    color: 'bg-blue-50 text-blue-600 border-blue-200',
    badge: 'bg-blue-500 text-white',
    description: 'Can edit the story, gallery, and events. Cannot delete the memorial or manage co-admins.',
    permissions: [
      { icon: FiEdit3, label: 'Edit story & showcase' },
      { icon: FiImage, label: 'Upload & manage gallery' },
      { icon: FiUsers, label: 'Create & edit events' },
    ],
  },
  moderator: {
    id: 'moderator',
    label: 'Moderator',
    icon: FiMessageSquare,
    color: 'bg-amber-50 text-amber-600 border-amber-200',
    badge: 'bg-amber-500 text-white',
    description: 'Can approve or decline tributes and manage the guestbook. Cannot edit memorial content.',
    permissions: [
      { icon: FiCheck, label: 'Approve / decline tributes' },
      { icon: FiMessageSquare, label: 'Moderate guestbook' },
      { icon: FiShield, label: 'Remove inappropriate content' },
    ],
  },
  contributor: {
    id: 'contributor',
    label: 'Contributor',
    icon: FiImage,
    color: 'bg-green-50 text-green-600 border-green-200',
    badge: 'bg-green-500 text-white',
    description: 'Can add photos and leave tributes. Cannot edit memorial content or moderate.',
    permissions: [
      { icon: FiImage, label: 'Add photos to gallery' },
      { icon: FiMessageSquare, label: 'Leave tributes' },
      { icon: FiUsers, label: 'Sign guestbook' },
    ],
  },
};

const STORAGE_PREFIX = 'memorial_coadmins_';

// ── Helpers ───────────────────────────────────────────────────────────────────
function loadCoAdmins(memorialId) {
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + memorialId);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveCoAdmins(memorialId, list) {
  localStorage.setItem(STORAGE_PREFIX + memorialId, JSON.stringify(list));
}

// ── Role picker chips ─────────────────────────────────────────────────────────
function RolePicker({ value, onChange, compact }) {
  return (
    <div className={`flex flex-wrap ${compact ? 'gap-1.5' : 'gap-2'}`}>
      {Object.values(CO_ADMIN_ROLES).map((role) => {
        const active = value === role.id;
        return (
          <button
            key={role.id}
            type="button"
            onClick={() => onChange(role.id)}
            className={`flex items-center gap-1.5 rounded-xl border-2 transition-all ${
              compact ? 'px-2.5 py-1.5 text-[8px]' : 'px-4 py-2.5 text-[10px]'
            } font-black uppercase tracking-widest ${
              active
                ? `${role.badge} border-transparent shadow-sm`
                : 'border-brand-dark bg-white text-text-muted hover:border-accent-gold/40'
            }`}
          >
            <SafeIcon icon={role.icon} className={compact ? 'w-3 h-3' : 'w-3.5 h-3.5'} />
            {role.label}
          </button>
        );
      })}
    </div>
  );
}

// ── Permission breakdown for a role ──────────────────────────────────────────
function PermissionList({ roleId }) {
  const role = CO_ADMIN_ROLES[roleId];
  if (!role) return null;
  return (
    <div className="flex flex-wrap gap-2">
      {role.permissions.map((perm, i) => (
        <div
          key={i}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-brand-light/50 rounded-lg border border-brand-dark text-[10px] font-bold text-accent-navy"
        >
          <SafeIcon icon={perm.icon} className="w-3 h-3 text-accent-gold" />
          {perm.label}
        </div>
      ))}
    </div>
  );
}

// ── Co-admin row ─────────────────────────────────────────────────────────────
function CoAdminRow({ admin, onRoleChange, onRemove, isOwner }) {
  const [expanded, setExpanded] = useState(false);
  const role = CO_ADMIN_ROLES[admin.role] || CO_ADMIN_ROLES.contributor;
  const isPending = admin.status === 'Pending';

  return (
    <motion.div
      layout
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, scale: 0.95 }}
      className="bg-white border-2 border-brand-dark rounded-2xl overflow-hidden hover:border-accent-gold/30 transition-all"
    >
      {/* Main row */}
      <div className="flex items-center gap-4 p-4">
        {/* Avatar */}
        <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${role.color} border`}>
          <SafeIcon icon={role.icon} className="w-5 h-5" />
        </div>

        {/* Email + status */}
        <div className="flex-grow min-w-0">
          <p className="text-sm font-bold text-accent-navy truncate">{admin.email}</p>
          <div className="flex items-center gap-2 mt-0.5">
            <span className={`text-[8px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full ${role.badge}`}>
              {role.label}
            </span>
            {isPending ? (
              <span className="flex items-center gap-1 text-[9px] font-black uppercase tracking-widest text-amber-500">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
                Pending
              </span>
            ) : (
              <span className="flex items-center gap-1 text-[9px] font-black uppercase tracking-widest text-green-600">
                <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                Active
              </span>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <button
            onClick={() => setExpanded((p) => !p)}
            className="p-2 text-text-muted hover:text-accent-navy hover:bg-brand-light rounded-lg transition-colors"
            title={expanded ? 'Hide permissions' : 'Show permissions'}
          >
            <SafeIcon icon={expanded ? FiChevronUp : FiChevronDown} className="w-4 h-4" />
          </button>
          {isOwner && (
            <button
              onClick={() => onRemove(admin.email)}
              className="p-2 text-text-muted hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
              title="Remove co-admin"
            >
              <SafeIcon icon={FiTrash2} className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Expanded section */}
      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4 pt-1 border-t border-brand-dark/30 space-y-3">
              {/* Role description */}
              <p className="text-[11px] text-text-muted leading-relaxed italic pt-2">{role.description}</p>

              {/* Permissions */}
              <PermissionList roleId={admin.role} />

              {/* Role changer (owner only) */}
              {isOwner && (
                <div className="pt-2">
                  <p className="text-[9px] font-black uppercase tracking-widest text-accent-navy/60 mb-2">Change Role</p>
                  <RolePicker value={admin.role} onChange={(r) => onRoleChange(admin.email, r)} compact />
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// ── Main panel ────────────────────────────────────────────────────────────────
const CoAdminRolesPanel = ({ memorial }) => {
  const memId = memorial?.id || 'default';

  const [coAdmins, setCoAdmins] = useState(() => loadCoAdmins(memId));
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('editor');
  const [emailError, setEmailError] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [inviteSent, setInviteSent] = useState(false);

  useEffect(() => {
    setCoAdmins(loadCoAdmins(memId));
  }, [memId]);

  useEffect(() => {
    saveCoAdmins(memId, coAdmins);
  }, [coAdmins, memId]);

  const handleInvite = (e) => {
    e.preventDefault();
    setEmailError('');

    if (!inviteEmail) return;

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(inviteEmail)) {
      setEmailError('Please enter a valid email address');
      return;
    }

    if (coAdmins.some((a) => a.email.toLowerCase() === inviteEmail.toLowerCase())) {
      setEmailError('This email is already a co-admin');
      return;
    }

    if (coAdmins.length >= 10) {
      setEmailError('Maximum of 10 co-admins reached');
      return;
    }

    setIsSending(true);
    setTimeout(() => {
      setCoAdmins((prev) => [
        ...prev,
        {
          email: inviteEmail,
          role: inviteRole,
          status: 'Pending',
          invitedAt: new Date().toISOString(),
        },
      ]);
      setIsSending(false);
      setInviteSent(true);
      setInviteEmail('');
      setTimeout(() => {
        setInviteSent(false);
        setShowInvite(false);
      }, 1800);
    }, 1200);
  };

  const handleRoleChange = (email, newRole) => {
    setCoAdmins((prev) =>
      prev.map((a) => (a.email === email ? { ...a, role: newRole } : a))
    );
  };

  const handleRemove = (email) => {
    if (window.confirm(`Remove ${email} as co-admin?`)) {
      setCoAdmins((prev) => prev.filter((a) => a.email !== email));
    }
  };

  const handleActivate = (email) => {
    setCoAdmins((prev) =>
      prev.map((a) => (a.email === email ? { ...a, status: 'Active' } : a))
    );
  };

  const roleCounts = coAdmins.reduce((acc, a) => {
    acc[a.role] = (acc[a.role] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="bg-white p-8 md:p-12 rounded-[40px] shadow-sm border border-brand-dark overflow-hidden relative">
      <div className="absolute top-0 right-0 w-40 h-40 bg-purple-500/5 rounded-full -mr-20 -mt-20 blur-3xl" />

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-purple-50 text-purple-600 rounded-2xl flex items-center justify-center shadow-sm">
            <SafeIcon icon={FiUsers} className="w-6 h-6" />
          </div>
          <div>
            <h3 className="text-2xl font-serif text-accent-navy">Co-Admin Roles</h3>
            <p className="text-sm text-text-muted font-light">
              Delegate memorial management to trusted collaborators with scoped permissions.
            </p>
          </div>
        </div>
        <button
          onClick={() => setShowInvite((p) => !p)}
          className="flex items-center gap-2 px-6 py-3 bg-purple-600 text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-purple-700 transition-all shadow-lg shadow-purple-100 flex-shrink-0"
        >
          <SafeIcon icon={FiUserPlus} className="w-4 h-4" />
          Invite Co-Admin
        </button>
      </div>

      {/* Role legend */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-8">
        {Object.values(CO_ADMIN_ROLES).map((role) => (
          <div
            key={role.id}
            className={`p-4 rounded-2xl border-2 transition-all ${
              (roleCounts[role.id] || 0) > 0
                ? `${role.color} border-current`
                : 'border-brand-dark bg-brand-light/30 opacity-70'
            }`}
          >
            <div className="flex items-center gap-2 mb-1">
              <SafeIcon icon={role.icon} className="w-4 h-4" />
              <p className="text-[10px] font-black uppercase tracking-widest">{role.label}</p>
              {(roleCounts[role.id] || 0) > 0 && (
                <span className="ml-auto text-[10px] font-black bg-white/60 rounded-full px-2 py-0.5">
                  {roleCounts[role.id]}
                </span>
              )}
            </div>
            <p className="text-[10px] text-text-muted leading-snug font-light">{role.description}</p>
          </div>
        ))}
      </div>

      {/* Invite form */}
      <AnimatePresence>
        {showInvite && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden"
          >
            <div className="bg-brand-light/30 border-2 border-brand-dark rounded-[28px] p-6 mb-6">
              {inviteSent ? (
                <div className="text-center py-6">
                  <div className="w-14 h-14 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-3">
                    <SafeIcon icon={FiCheck} className="w-7 h-7" />
                  </div>
                  <h4 className="text-lg font-serif text-accent-navy mb-1">Invitation Sent</h4>
                  <p className="text-sm text-text-muted">They will receive an email to join as a co-admin.</p>
                </div>
              ) : (
                <form onSubmit={handleInvite} className="space-y-5">
                  <div>
                    <p className="text-[10px] font-black uppercase tracking-widest text-accent-navy/60 mb-2">
                      Co-Admin Email
                    </p>
                    <div className="relative">
                      <SafeIcon icon={FiMail} className="absolute left-4 top-1/2 -translate-y-1/2 text-text-muted w-4 h-4" />
                      <input
                        type="email"
                        required
                        value={inviteEmail}
                        onChange={(e) => {
                          setInviteEmail(e.target.value);
                          if (emailError) setEmailError('');
                        }}
                        placeholder="collaborator@email.com"
                        className={`w-full pl-11 pr-4 py-3.5 bg-white border-2 rounded-2xl outline-none text-sm font-bold transition-all ${
                          emailError ? 'border-red-400' : 'border-brand-dark focus:border-purple-500'
                        }`}
                      />
                    </div>
                    {emailError && (
                      <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="text-[10px] text-red-500 font-black uppercase tracking-wider mt-1.5 ml-1"
                      >
                        {emailError}
                      </motion.p>
                    )}
                  </div>

                  <div>
                    <p className="text-[10px] font-black uppercase tracking-widest text-accent-navy/60 mb-2">
                      Assign Role
                    </p>
                    <RolePicker value={inviteRole} onChange={setInviteRole} />
                    <div className="mt-3 p-3 bg-white rounded-xl border border-brand-dark">
                      <p className="text-[10px] text-text-muted leading-relaxed italic">
                        {CO_ADMIN_ROLES[inviteRole]?.description}
                      </p>
                      <div className="mt-2">
                        <PermissionList roleId={inviteRole} />
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-3 pt-1">
                    <button
                      type="submit"
                      disabled={isSending || !inviteEmail}
                      className="flex-1 bg-purple-600 text-white py-3.5 rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-purple-700 transition-all shadow-lg shadow-purple-100 flex items-center justify-center gap-2 disabled:opacity-60"
                    >
                      {isSending ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          Sending…
                        </>
                      ) : (
                        <>
                          <SafeIcon icon={FiSend} className="w-3.5 h-3.5" />
                          Send Invitation
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowInvite(false);
                        setInviteEmail('');
                        setEmailError('');
                      }}
                      className="px-6 py-3.5 bg-white border-2 border-brand-dark text-text-muted rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-brand-light transition-all"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Co-admin list */}
      {coAdmins.length > 0 ? (
        <div className="space-y-3">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-accent-navy/40">
              Active Co-Admins ({coAdmins.length}/10)
            </h4>
            <span className="text-[9px] font-black text-green-600 uppercase tracking-widest flex items-center gap-1">
              <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />
              Managed Locally
            </span>
          </div>
          <AnimatePresence initial={false}>
            {coAdmins.map((admin) => (
              <CoAdminRow
                key={admin.email}
                admin={admin}
                onRoleChange={handleRoleChange}
                onRemove={handleRemove}
                isOwner={true}
              />
            ))}
          </AnimatePresence>

          {/* Activate pending demo hint */}
          {coAdmins.some((a) => a.status === 'Pending') && (
            <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-2xl text-amber-800">
              <SafeIcon icon={FiClock} className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-[11px] font-bold mb-1">Pending invitations</p>
                <p className="text-[10px] leading-relaxed">
                  Pending co-admins must accept their email invitation. For demo purposes, you can manually
                  activate them:
                </p>
                <div className="flex flex-wrap gap-2 mt-2">
                  {coAdmins
                    .filter((a) => a.status === 'Pending')
                    .map((a) => (
                      <button
                        key={a.email}
                        onClick={() => handleActivate(a.email)}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-amber-300 rounded-lg text-[9px] font-black uppercase tracking-widest text-amber-700 hover:bg-amber-100 transition-colors"
                      >
                        <SafeIcon icon={FiCheck} className="w-3 h-3" />
                        Activate {a.email.split('@')[0]}
                      </button>
                    ))}
                </div>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="p-10 bg-brand-light/20 rounded-[32px] border-2 border-dashed border-brand-dark flex flex-col items-center text-center">
          <div className="w-16 h-16 bg-white border-2 border-brand-dark rounded-[24px] flex items-center justify-center text-purple-300 mb-6">
            <SafeIcon icon={FiUserPlus} className="w-8 h-8" />
          </div>
          <h4 className="text-lg font-serif text-accent-navy mb-2">No Co-Admins Yet</h4>
          <p className="text-xs text-text-muted max-w-sm font-light leading-relaxed mb-8">
            Invite trusted collaborators to help manage this memorial. Each role has a specific set of
            permissions tailored to their responsibility.
          </p>
          <button
            onClick={() => setShowInvite(true)}
            className="py-4 px-10 bg-purple-600 text-white rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-purple-700 transition-all shadow-lg shadow-purple-100"
          >
            Invite First Co-Admin
          </button>
        </div>
      )}

      {/* Info note */}
      <div className="mt-6 flex items-start gap-3 p-4 bg-accent-navy/[0.03] rounded-2xl border border-dashed border-brand-dark">
        <SafeIcon icon={FiInfo} className="w-4 h-4 text-accent-navy/40 flex-shrink-0 mt-0.5" />
        <p className="text-[11px] text-text-muted leading-relaxed">
          Co-admin invitations and role assignments are stored locally for this memorial. In production, an
          email is sent to the invitee and they must accept before gaining access. Only the memorial owner
          can change roles or remove co-admins.
        </p>
      </div>
    </div>
  );
};

export default CoAdminRolesPanel;